import { useState, type FormEvent } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db702c4jfl1ikpv8spig";

type Status = "idle" | "submitting" | "success" | "error";

const TOPICS = ["视频内容建议", "合作与商务", "投稿推荐", "其他"];

export default function ContactForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    // anti-spam honeypot
    const honeypot = String(formData.get("company_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setMessage("留言已提交，感谢你的反馈！");
      form.reset();
      return;
    }

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "company_alt") return;
      if (typeof value === "string" && value.trim() !== "") {
        params.append(key, value);
      }
    });

    setStatus("submitting");
    setMessage("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (!response.ok || parsed?.code !== "OK" || isSpam) {
        setStatus("error");
        setMessage(
          typeof serverMsg === "string" && serverMsg.trim()
            ? serverMsg
            : "提交失败，请检查内容后稍后重试。",
        );
        return;
      }

      setStatus("success");
      setMessage("留言已提交，感谢你的反馈！我会尽快回复你。");
      form.reset();
    } catch {
      setStatus("error");
      setMessage("网络异常，提交失败，请稍后重试。");
    }
  };

  return (
    <form
      id="contact-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-5 md:p-7"
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="contact-name" className="text-sm font-medium text-foreground-800">
            你的称呼 <span className="text-primary-600">*</span>
          </label>
          <input
            id="contact-name"
            name="name"
            type="text"
            required
            placeholder="怎么称呼你？"
            className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="contact-email" className="text-sm font-medium text-foreground-800">
            电子邮箱 <span className="text-primary-600">*</span>
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            required
            placeholder="you@example.com"
            className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
          />
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor="contact-topic" className="text-sm font-medium text-foreground-800">
          留言主题
        </label>
        <select
          id="contact-topic"
          name="topic"
          defaultValue={TOPICS[0]}
          className="cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-400"
        >
          {TOPICS.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor="contact-message" className="text-sm font-medium text-foreground-800">
          留言内容 <span className="text-primary-600">*</span>
        </label>
        <textarea
          id="contact-message"
          name="message"
          required
          maxLength={500}
          rows={5}
          placeholder="想对糖心说的话、想看的内容，都可以写在这里（最多 500 字）"
          className="resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm leading-relaxed text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
        />
      </div>

      <input
        type="text"
        name="company_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="media-caption-note"
      />

      <button
        type="submit"
        disabled={status === "submitting"}
        className="flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
      >
        {status === "submitting" ? (
          <>
            <i className="ri-loader-4-line animate-spin" /> 提交中…
          </>
        ) : (
          <>
            <i className="ri-send-plane-fill" /> 提交留言
          </>
        )}
      </button>

      {message && (
        <p
          className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm ${
            status === "success"
              ? "bg-primary-100 text-primary-800"
              : "bg-secondary-100 text-secondary-900"
          }`}
        >
          <i
            className={
              status === "success" ? "ri-checkbox-circle-line" : "ri-error-warning-line"
            }
          />
          {message}
        </p>
      )}
    </form>
  );
}